const { mapValues, groupBy, sumBy, isEmpty, keyBy, pickBy, } = require('lodash');
const { format: formatDate, differenceInDays, addDays, startOfDay, } = require('date-fns');

const { keys } = Object;

const computeLeftQuantities = (purchaseOrders, containers) => {
  const containerItemsGroupedByPoId = groupBy(containers.flatMap(c => c.items.map(_ => ({ ..._, container: c, }))), 'purchaseOrderId');
  const computedPurchaseOrders = purchaseOrders.map((purchaseOrder) => {
    const computedItems = purchaseOrder.items.map((item) => {
      const containerItems = (containerItemsGroupedByPoId[purchaseOrder.id] || []).filter(_ => _.productId === item.productId);
      return {
        ...item,
        containerItems,
        leftQuantity: item.quantity - sumBy(containerItems, 'quantity'),
      };
    });
    return {
      ...purchaseOrder,
      computedItems,
    };
  });
  return computedPurchaseOrders;
};

const computeSchedule = (purchaseOrders, containers, { isActual = false } = {}) => {
  const nullDate = new Date(1900, 0, 1);
  const purchaseOrderItemsGroupedByProductId = groupBy((
    purchaseOrders.map(_ => ({
      ..._,
      completedMonth: formatDate(
        (isActual ? (_.confirmedFields?.completedOn ? _.completedOn?.toDate() : null) : _.completedOn?.toDate()) || nullDate,
        'yyyyMM'
      ),
    }))
  ).flatMap(p => p.items.map(_ => ({ ..._, purchaseOrder: p }))), 'productId');
  const containerItemsGroupedByProductId = groupBy((
    containers.map(_ => ({ ..._, arrivalMonth: formatDate(_.arrivalDate.toDate(), 'yyyyMM'), }))
  ).flatMap(c => c.items.map(_ => ({ ..._, container: c }))), 'productId');
  return mapValues(purchaseOrderItemsGroupedByProductId, (purchaseOrderItems, productId) => {
    const containerItems = containerItemsGroupedByProductId[productId] || [];
    const completion = mapValues(
      groupBy(purchaseOrderItems.filter(_ => _.purchaseOrder.completedMonth !== formatDate(nullDate, 'yyyyMM')), 'purchaseOrder.completedMonth'),
      _ => sumBy(_, 'quantity')
    );
    const arrival = mapValues(
      groupBy(containerItems, 'container.arrivalMonth'),
      _ => sumBy(_, 'quantity'),
    );
    const transferingCount = (date) => {
      return sumBy(containerItems.filter(_ => _.container.departureDate.toDate() <= date && _.container.arrivalDate.toDate() > date), 'quantity');
    };
    const departureCount = (date) => {
      return sumBy(containerItems.filter(_ => _.container.departureDate.toDate() <= date), 'quantity');
    };
    const abroadInventoryCount = (date) => {
      const filteredItems = isActual
        ? purchaseOrderItems.filter(_ => _.purchaseOrder.confirmedFields?.completedOn && _.purchaseOrder.completedOn?.toDate() <= date)
        : purchaseOrderItems.filter(_ => _.purchaseOrder.completedOn?.toDate() <= date);
      return sumBy(filteredItems, 'quantity') - departureCount(date);
    };
    return {
      completion,
      arrival,
      transferingCount,
      abroadInventoryCount,
    };
  });
};

const fields = ({ suppliers, }) => {
    const suppliersById = keyBy(suppliers, 'id');
    const isHidden = (fieldName) => (values) => {
      const supplier = suppliersById[values.supplierId];
      if (!supplier?.customizesPoDateFields) return false;
      return !(supplier.visiblePoDateFields || []).includes(fieldName);
    };
    return {
      poNumber: {
        label: 'PO No.',
        type: 'string',
        validations: {
          required: v => !isEmpty(v),
        },
      },
      supplierId: {
        label: '仕入先',
        type: 'select',
        options: suppliers.map(_ => ({ label: _.name, value: _.id })),
        validations: {
          required: v => !isEmpty(v),
        },
      },
      orderedOn: {
        label: '発注日',
        type: 'date',
        readOnly: _ => true,
      },
      piReceivedOn: {
        label: 'PI受信日',
        type: 'date',
        confirmable: true,
        hidden: isHidden('piReceivedOn'),
      },
      advancePaymentOn: {
        label: '前金支払日',
        type: 'date',
        confirmable: true,
        hidden: isHidden('advancePaymentOn'),
      },
      scheduledManufactureDate: {
        label: '製造日',
        type: 'date',
        confirmable: true,
        validations: {
          required: v => v != null,
        },
      },
      colorSpecificationDate: {
        label: '色指定日',
        type: 'date',
        confirmable: true,
        hidden: isHidden('colorSpecificationDate'),
        validations: {
          required: v => v != null,
        },
      },
      tireArrivalDate: {
        label: 'タイヤ入荷日',
        type: 'date',
        confirmable: true,
        hidden: isHidden('tireArrivalDate'),
        validations: {
          required: v => v != null,
        },
      },
      frameArrivalDate: {
        label: 'フレーム入荷日',
        type: 'date',
        confirmable: true,
        hidden: isHidden('frameArrivalDate'),
        validations: {
          required: v => v != null,
        },
      },
      otherPartsArrivalDate: {
        label: 'その他パーツ入荷日',
        type: 'date',
        confirmable: true,
        hidden: isHidden('otherPartsArrivalDate'),
        validations: {
          required: v => v != null,
        },
      },
      completedOn: {
        label: '完成日',
        type: 'date',
        confirmable: true,
        validations: {
          required: v => v != null,
        },
      },
      onlineInspectionDate: {
        label: 'オンライン検品日',
        type: 'date',
        confirmable: true,
        hidden: isHidden('onlineInspectionDate'),
      },
      onlineInspectionResult: {
        label: 'オンライン検品結果',
        type: 'text',
        rows: 3,
        hidden: isHidden('onlineInspectionResult'),
      },
      balancePaymentOn: {
        label: '残金支払日',
        type: 'date',
        confirmable: true,
        hidden: isHidden('balancePaymentOn'),
        validations: {
          required: v => v != null,
        },
      },
      requestEtd: {
        label: '工場出荷希望日',
        type: 'date',
        confirmable: true,
        hidden: isHidden('requestEtd'),
      },
      info: {
        label: 'Request',
        type: 'text',
        rows: 5,
      },
      note: {
        label: '社内メモ',
        type: 'text',
        rows: 5,
      },
    };
};

const customizableFieldNames = keys(pickBy(fields({ suppliers: [] }), _ => _.hidden));

const isFieldVisible = (supplier, fieldName) => {
  if (!supplier?.customizesPoDateFields) return true;
  if (!customizableFieldNames.includes(fieldName)) return true;
  return (supplier.visiblePoDateFields || []).includes(fieldName);
};

const computeAlerts = (purchaseOrder, supplier, now = new Date()) => {
  const { orderedOn, piReceivedOn, advancePaymentOn, confirmedFields = {}, } = purchaseOrder;
  const overdue = [
    orderedOn && isFieldVisible(supplier, 'piReceivedOn') && !piReceivedOn && differenceInDays(now, orderedOn.toDate()) >= 14 && 'piReceivedOn',
    orderedOn && isFieldVisible(supplier, 'advancePaymentOn') && !advancePaymentOn && differenceInDays(now, orderedOn.toDate()) >= 21 && 'advancePaymentOn',
  ].filter(Boolean);
  const today = startOfDay(now);
  const unconfirmed = keys(pickBy(fields({ suppliers: [] }), (v, k) => v.confirmable && isFieldVisible(supplier, k) && purchaseOrder[k] && !confirmedFields[k] && purchaseOrder[k].toDate() < today))
    .map(_ => `${_}:unconfirmed`);
  return [...overdue, ...unconfirmed];
};

module.exports = {
  fields,
  itemFields: ({ products, }) => {
    return {
      productId: {
        label: '商品',
        type: 'select',
        options: products.map(_ => ({ label: _.code, value: _.id })),
        validations: {
          required: v => !isEmpty(v),
        },
      },
      quantity: {
        label: '数量',
        type: 'integer',
        validations: {
          greaterThanOrEqualTo0: v => v != null && v >= 0,
        },
        showsTextInput: true,
      },
    };
  },
  computeAlerts,
  computeLeftQuantities,
  computeSchedule,
};
